export const localizedSeoLocales = [
  "ja",
  "ko",
  "de",
  "es",
  "fr",
  "pl",
  "nl",
  "pt",
  "ru",
  "zh",
  "zh-tw",
] as const;

export type LocalizedSeoLocale = (typeof localizedSeoLocales)[number];

export const localizedLandingSlugs = [
  "url-to-faceless-video",
  "script-to-faceless-video",
  "long-form-faceless-video-generator",
  "faceless-video-automation",
  "faceless-youtube-video-generator",
  "faceless-tiktok-video-generator",
  "faceless-instagram-reels-generator",
  "faceless-podcast-video-generator",
] as const;

export type LocalizedLandingSlug = (typeof localizedLandingSlugs)[number];

export type SeoCompetition = "low-medium" | "medium" | "medium-high" | "high";
export type SeoRolloutWave = 1 | 2 | 3;

export interface LocalizedSeoStrategy {
  languageTag: string;
  market: string;
  primaryKeyword: string;
  secondaryKeywords: string[];
  competition: SeoCompetition;
  rolloutWave: SeoRolloutWave;
  notes: string;
  landingKeywords: Record<LocalizedLandingSlug, string>;
}

export const localizedSeoStrategies: Record<
  LocalizedSeoLocale,
  LocalizedSeoStrategy
> = {
  ja: {
    languageTag: "ja-JP",
    market: "Japan",
    primaryKeyword: "顔出しなしAI動画生成ツール",
    secondaryKeywords: [
      "顔出し不要のAI動画",
      "顔出しなしYouTube動画",
      "フェイスレス動画生成",
    ],
    competition: "medium",
    rolloutWave: 1,
    notes:
      "Use 顔出しなし and 顔出し不要 in customer-facing copy. フェイスレス is useful as a supporting imported term, not the sole primary phrase.",
    landingKeywords: {
      "url-to-faceless-video": "URLから顔出しなし動画を作成",
      "script-to-faceless-video": "台本から顔出しなし動画を作成",
      "long-form-faceless-video-generator": "長尺の顔出しなし動画生成",
      "faceless-video-automation": "顔出しなし動画の自動化",
      "faceless-youtube-video-generator": "顔出しなしYouTube動画生成",
      "faceless-tiktok-video-generator": "顔出しなしTikTok動画生成",
      "faceless-instagram-reels-generator":
        "顔出しなしInstagramリール生成",
      "faceless-podcast-video-generator": "AIポッドキャスト動画生成",
    },
  },
  ko: {
    languageTag: "ko-KR",
    market: "South Korea",
    primaryKeyword: "AI 얼굴 없는 영상 생성기",
    secondaryKeywords: [
      "얼굴 없는 유튜브 채널",
      "AI 얼굴 없는 영상 만들기",
      "페이스리스 영상 제작",
    ],
    competition: "medium",
    rolloutWave: 2,
    notes:
      "얼굴 없는 영상 is the clearest native commercial phrase. 페이스리스 is established among creator audiences and should appear as a secondary term.",
    landingKeywords: {
      "url-to-faceless-video": "URL을 얼굴 없는 영상으로 변환",
      "script-to-faceless-video": "대본을 얼굴 없는 영상으로 변환",
      "long-form-faceless-video-generator": "장편 얼굴 없는 영상 생성기",
      "faceless-video-automation": "얼굴 없는 영상 자동화",
      "faceless-youtube-video-generator": "얼굴 없는 YouTube 영상 생성기",
      "faceless-tiktok-video-generator": "얼굴 없는 TikTok 영상 생성기",
      "faceless-instagram-reels-generator":
        "얼굴 없는 Instagram 릴스 생성기",
      "faceless-podcast-video-generator": "AI 팟캐스트 영상 생성기",
    },
  },
  de: {
    languageTag: "de-DE",
    market: "Germany",
    primaryKeyword: "KI-Videogenerator für gesichtslose Videos",
    secondaryKeywords: [
      "gesichtslose KI-Videos erstellen",
      "Videos ohne Gesicht erstellen",
      "Faceless Videos erstellen",
    ],
    competition: "medium-high",
    rolloutWave: 1,
    notes:
      "Prefer natural compound phrasing over machine-translated forms such as Gesichtsloser-Video-Generator. Keep Faceless as a supporting creator term.",
    landingKeywords: {
      "url-to-faceless-video": "URL in gesichtsloses Video umwandeln",
      "script-to-faceless-video": "Skript in gesichtsloses Video umwandeln",
      "long-form-faceless-video-generator":
        "Generator für lange gesichtslose Videos",
      "faceless-video-automation": "Automatisierung gesichtsloser Videos",
      "faceless-youtube-video-generator":
        "Generator für gesichtslose YouTube-Videos",
      "faceless-tiktok-video-generator":
        "Generator für gesichtslose TikTok-Videos",
      "faceless-instagram-reels-generator":
        "Generator für gesichtslose Instagram Reels",
      "faceless-podcast-video-generator": "KI-Podcast-Video-Generator",
    },
  },
  es: {
    languageTag: "es-ES",
    market: "Spain",
    primaryKeyword: "generador de vídeos sin rostro con IA",
    secondaryKeywords: [
      "crear vídeos sin mostrar el rostro",
      "creador de vídeos sin rostro",
      "vídeos faceless con IA",
    ],
    competition: "high",
    rolloutWave: 1,
    notes:
      "Use vídeos sin rostro as the primary Spanish phrase. Sin mostrar el rostro is a strong explanatory variant; faceless can support creator-oriented copy.",
    landingKeywords: {
      "url-to-faceless-video": "convertir URL en vídeo sin rostro",
      "script-to-faceless-video": "guion a vídeo sin rostro",
      "long-form-faceless-video-generator":
        "generador de vídeos largos sin rostro",
      "faceless-video-automation": "automatización de vídeos sin rostro",
      "faceless-youtube-video-generator":
        "generador de vídeos de YouTube sin rostro",
      "faceless-tiktok-video-generator":
        "generador de vídeos de TikTok sin rostro",
      "faceless-instagram-reels-generator":
        "generador de Reels de Instagram sin rostro",
      "faceless-podcast-video-generator":
        "generador de vídeos de podcast con IA",
    },
  },
  fr: {
    languageTag: "fr-FR",
    market: "France",
    primaryKeyword: "générateur de vidéos sans visage avec IA",
    secondaryKeywords: [
      "créer une vidéo sans visage",
      "créateur de vidéos sans visage",
      "vidéos faceless avec IA",
    ],
    competition: "high",
    rolloutWave: 1,
    notes:
      "Vidéos sans visage is the dominant localized phrase across major tools. Faceless is best retained only as a secondary creator term.",
    landingKeywords: {
      "url-to-faceless-video": "URL en vidéo sans visage",
      "script-to-faceless-video": "script en vidéo sans visage",
      "long-form-faceless-video-generator":
        "générateur de vidéos longues sans visage",
      "faceless-video-automation": "automatisation de vidéos sans visage",
      "faceless-youtube-video-generator":
        "générateur de vidéos YouTube sans visage",
      "faceless-tiktok-video-generator":
        "générateur de vidéos TikTok sans visage",
      "faceless-instagram-reels-generator":
        "générateur de Reels Instagram sans visage",
      "faceless-podcast-video-generator":
        "générateur de vidéos podcast avec IA",
    },
  },
  pl: {
    languageTag: "pl-PL",
    market: "Poland",
    primaryKeyword: "generator wideo bez twarzy AI",
    secondaryKeywords: [
      "generator filmów bez twarzy",
      "filmy bez twarzy z AI",
      "tworzenie filmów bez pokazywania twarzy",
    ],
    competition: "low-medium",
    rolloutWave: 2,
    notes:
      "Filmów bez twarzy is more natural for the commercial head term. Use wideo in supporting product copy where it reads more naturally.",
    landingKeywords: {
      "url-to-faceless-video": "URL na film bez twarzy",
      "script-to-faceless-video": "scenariusz na film bez twarzy",
      "long-form-faceless-video-generator":
        "generator długich filmów bez twarzy",
      "faceless-video-automation": "automatyzacja filmów bez twarzy",
      "faceless-youtube-video-generator":
        "generator filmów YouTube bez twarzy",
      "faceless-tiktok-video-generator":
        "generator filmów TikTok bez twarzy",
      "faceless-instagram-reels-generator":
        "generator Instagram Reels bez twarzy",
      "faceless-podcast-video-generator":
        "generator filmów podcastowych AI",
    },
  },
  nl: {
    languageTag: "nl-NL",
    market: "Netherlands",
    primaryKeyword: "AI gezichtsloze videogenerator",
    secondaryKeywords: [
      "gezichtsloze videomaker",
      "faceless video maken",
      "video zonder gezicht maken",
    ],
    competition: "medium",
    rolloutWave: 2,
    notes:
      "Dutch product SERPs lead with gezichtsloze videogenerator and gezichtsloze videomaker. Keep faceless video as a supporting creator term.",
    landingKeywords: {
      "url-to-faceless-video": "URL naar gezichtsloze video",
      "script-to-faceless-video": "script naar gezichtsloze video",
      "long-form-faceless-video-generator":
        "generator voor lange gezichtsloze video's",
      "faceless-video-automation": "gezichtsloze video's automatiseren",
      "faceless-youtube-video-generator":
        "gezichtsloze YouTube-videogenerator",
      "faceless-tiktok-video-generator":
        "gezichtsloze TikTok-videogenerator",
      "faceless-instagram-reels-generator":
        "gezichtsloze Instagram Reels maken",
      "faceless-podcast-video-generator": "AI-podcastvideogenerator",
    },
  },
  pt: {
    languageTag: "pt-BR",
    market: "Brazil",
    primaryKeyword: "gerador de vídeos sem rosto com IA",
    secondaryKeywords: [
      "gerador de vídeos para canal dark",
      "criar vídeos sem aparecer",
      "vídeos faceless com IA",
    ],
    competition: "high",
    rolloutWave: 1,
    notes:
      "Canal dark is a distinct high-intent Brazilian creator term. Use it in YouTube and educational content while keeping vídeos sem rosto as the product head term.",
    landingKeywords: {
      "url-to-faceless-video": "URL para vídeo sem rosto",
      "script-to-faceless-video": "roteiro para vídeo sem rosto",
      "long-form-faceless-video-generator":
        "gerador de vídeos longos sem rosto",
      "faceless-video-automation": "automação de vídeos sem rosto",
      "faceless-youtube-video-generator":
        "gerador de vídeos para YouTube sem rosto",
      "faceless-tiktok-video-generator":
        "gerador de vídeos para TikTok sem rosto",
      "faceless-instagram-reels-generator":
        "gerador de Reels sem rosto",
      "faceless-podcast-video-generator":
        "gerador de vídeos de podcast com IA",
    },
  },
  ru: {
    languageTag: "ru-RU",
    market: "Russia",
    primaryKeyword: "ИИ-генератор видео без лица",
    secondaryKeywords: [
      "создать видео без лица",
      "безликие видео для YouTube",
      "генератор анонимных видео",
    ],
    competition: "medium",
    rolloutWave: 3,
    notes:
      "Видео без лица is the clearest commercial phrase. Безликие видео is useful in editorial copy but can sound less product-focused.",
    landingKeywords: {
      "url-to-faceless-video": "URL в видео без лица",
      "script-to-faceless-video": "сценарий в видео без лица",
      "long-form-faceless-video-generator":
        "генератор длинных видео без лица",
      "faceless-video-automation": "автоматизация видео без лица",
      "faceless-youtube-video-generator":
        "генератор видео без лица для YouTube",
      "faceless-tiktok-video-generator":
        "генератор видео без лица для TikTok",
      "faceless-instagram-reels-generator":
        "генератор Reels без лица",
      "faceless-podcast-video-generator":
        "ИИ-генератор видео для подкаста",
    },
  },
  zh: {
    languageTag: "zh-CN",
    market: "Simplified Chinese",
    primaryKeyword: "AI 不露脸视频生成器",
    secondaryKeywords: [
      "无脸视频生成器",
      "YouTube 不露脸频道",
      "AI 自动生成短视频",
    ],
    competition: "low-medium",
    rolloutWave: 3,
    notes:
      "不露脸 is more natural in creator language than a literal 无脸 translation. Google reach mainly covers overseas Simplified Chinese users; mainland search requires a separate Baidu strategy.",
    landingKeywords: {
      "url-to-faceless-video": "链接转不露脸视频",
      "script-to-faceless-video": "脚本转不露脸视频",
      "long-form-faceless-video-generator": "长视频不露脸生成器",
      "faceless-video-automation": "不露脸视频自动化",
      "faceless-youtube-video-generator": "YouTube 不露脸视频生成器",
      "faceless-tiktok-video-generator": "TikTok 不露脸视频生成器",
      "faceless-instagram-reels-generator":
        "Instagram Reels 不露脸视频生成器",
      "faceless-podcast-video-generator": "AI 播客视频生成器",
    },
  },
  "zh-tw": {
    languageTag: "zh-TW",
    market: "Taiwan",
    primaryKeyword: "AI 不露臉影片產生器",
    secondaryKeywords: [
      "AI 無露臉影片產生器",
      "無臉影片產生器",
      "不露臉 YouTube",
    ],
    competition: "medium",
    rolloutWave: 2,
    notes:
      "Use 影片, 產生器, and 不露臉 for Taiwan. 無臉 appears on localized tool pages, while 不露臉 is more natural in creator education content.",
    landingKeywords: {
      "url-to-faceless-video": "網址轉不露臉影片",
      "script-to-faceless-video": "腳本轉不露臉影片",
      "long-form-faceless-video-generator": "長篇不露臉影片產生器",
      "faceless-video-automation": "不露臉影片自動化",
      "faceless-youtube-video-generator": "YouTube 不露臉影片產生器",
      "faceless-tiktok-video-generator": "TikTok 不露臉影片產生器",
      "faceless-instagram-reels-generator":
        "Instagram Reels 不露臉影片產生器",
      "faceless-podcast-video-generator": "AI Podcast 影片產生器",
    },
  },
};
